import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { brandColors } from '../../../../theme/theme';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

/**
 * MultiMetricTrends Component
 * 
 * Overlays all 7 new metrics across survey rounds
 * Toggle metrics on/off, demographic filtering, velocity indicators
 */
const MultiMetricTrends = ({ data, trendsData }) => {
  // Metric visibility toggles
  const [visibleMetrics, setVisibleMetrics] = useState({
    excessAwareness: true,
    outstandingInterest: true,
    awareOpeningWeekend: false,
    outstandingOpeningWeekend: false,
    theaterAmongAware: false,
    outstandingTheater: false,
    intentConversion: false
  });

  // Demographic filters
  const [selectedEthnicity, setSelectedEthnicity] = useState('all');
  const [selectedAge, setSelectedAge] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for multi-metric trends
        </Typography>
      </Paper>
    );
  }

  // Define metrics
  const metrics = [
    { 
      id: 'excessAwareness', 
      name: 'Excess Awareness', 
      color: '#f44336',
      countField: 'TotalExcessAwareness',
      baseField: 'TotalAware'
    },
    { 
      id: 'outstandingInterest', 
      name: 'Outstanding Interest', 
      color: '#ffc107',
      countField: 'TotalOutstandingInterest',
      baseField: 'TotalInterest'
    },
    { 
      id: 'awareOpeningWeekend', 
      name: 'Aware Opening Weekend', 
      color: '#9c27b0',
      countField: 'TotalAwareOpeningWeekend',
      baseField: 'TotalAware'
    },
    { 
      id: 'outstandingOpeningWeekend', 
      name: 'Outstanding Opening Weekend', 
      color: '#e91e63',
      countField: 'TotalOutstandingOpeningWeekend',
      baseField: 'TotalResponses'
    },
    { 
      id: 'theaterAmongAware', 
      name: 'Theater Among Aware', 
      color: '#2196f3',
      countField: 'TotalTheaterAmongAware',
      baseField: 'TotalAware'
    },
    { 
      id: 'outstandingTheater', 
      name: 'Outstanding Theater', 
      color: '#00bcd4',
      countField: 'TotalOutstandingTheater',
      baseField: 'TotalResponses'
    },
    { 
      id: 'intentConversion', 
      name: 'Intent Conversion', 
      color: '#4caf50',
      countField: 'TotalIntentConversion',
      baseField: 'TotalAware'
    }
  ];

  // Filter data by demographics
  const filteredData = data.filter(record => {
    if (selectedEthnicity !== 'all' && record.ethnicity !== selectedEthnicity) return false;
    if (selectedAge !== 'all' && record.agegroup !== selectedAge) return false;
    if (selectedGender !== 'all' && record.gender !== selectedGender) return false;
    return true;
  });

  // Group by film recency order (survey round)
  const roundsMap = {};
  filteredData.forEach(record => {
    const round = record.FilmRecencyOrder || 1;
    const surveyDate = record.SurveyCompleteDate || '';
    
    if (!roundsMap[round]) {
      roundsMap[round] = {
        round,
        surveyDate,
        totalResponses: 0,
        totalAware: 0,
        totalInterest: 0,
        totalExcessAwareness: 0,
        totalOutstandingInterest: 0,
        totalAwareOpeningWeekend: 0,
        totalOutstandingOpeningWeekend: 0,
        totalTheaterAmongAware: 0,
        totalOutstandingTheater: 0,
        totalIntentConversion: 0
      };
    }
    
    roundsMap[round].totalResponses += record.TotalResponses || 0;
    roundsMap[round].totalAware += record.TotalAware || 0;
    roundsMap[round].totalInterest += record.TotalInterest || 0;
    roundsMap[round].totalExcessAwareness += record.TotalExcessAwareness || 0;
    roundsMap[round].totalOutstandingInterest += record.TotalOutstandingInterest || 0;
    roundsMap[round].totalAwareOpeningWeekend += record.TotalAwareOpeningWeekend || 0;
    roundsMap[round].totalOutstandingOpeningWeekend += record.TotalOutstandingOpeningWeekend || 0;
    roundsMap[round].totalTheaterAmongAware += record.TotalTheaterAmongAware || 0;
    roundsMap[round].totalOutstandingTheater += record.TotalOutstandingTheater || 0;
    roundsMap[round].totalIntentConversion += record.TotalIntentConversion || 0;
  });

  // Convert to array and calculate percentages
  const roundsData = Object.values(roundsMap)
    .sort((a, b) => a.round - b.round)
    .map(round => {
      const result = {
        round: round.round,
        surveyDate: round.surveyDate,
        label: `Round ${round.round}`
      };
      
      // Calculate percentages for each metric
      metrics.forEach(metric => {
        const count = round[metric.countField.replace('Total', 'total')] || 0;
        const base = round[metric.baseField.replace('Total', 'total')] || 0;
        result[metric.id] = base > 0 ? (count / base) * 100 : 0;
      });
      
      return result;
    });

  // Calculate velocity (change from previous round)
  const velocity = {};
  if (roundsData.length >= 2) {
    const latest = roundsData[roundsData.length - 1];
    const previous = roundsData[roundsData.length - 2];
    
    metrics.forEach(metric => {
      const change = latest[metric.id] - previous[metric.id];
      velocity[metric.id] = change;
    });
  }

  // Get unique values for filters
  const ethnicities = [...new Set(data.map(r => r.ethnicity).filter(Boolean))];
  const ageGroups = [...new Set(data.map(r => r.agegroup).filter(Boolean))];
  const genders = [...new Set(data.map(r => r.gender).filter(Boolean))];

  // Handle metric toggle
  const handleMetricToggle = (metricId) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metricId]: !prev[metricId]
    }));
  };

  // Get velocity icon
  const getVelocityIcon = (change) => {
    if (Math.abs(change) < 1) return <TrendingFlat fontSize="small" />;
    if (change > 0) return <TrendingUp fontSize="small" sx={{ color: brandColors.success }} />;
    return <TrendingDown fontSize="small" sx={{ color: brandColors.error }} />;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Multi-Metric Trend Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare all 7 advanced metrics across survey rounds
        </Typography>
      </Box>

      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Demographic Filters */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Ethnicity</InputLabel>
            <Select
              value={selectedEthnicity}
              label="Ethnicity"
              onChange={(e) => setSelectedEthnicity(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {ethnicities.map(eth => (
                <MenuItem key={eth} value={eth}>{eth}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Age Group</InputLabel>
            <Select
              value={selectedAge}
              label="Age Group"
              onChange={(e) => setSelectedAge(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {ageGroups.map(age => (
                <MenuItem key={age} value={age}>{age}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Gender</InputLabel>
            <Select
              value={selectedGender}
              label="Gender"
              onChange={(e) => setSelectedGender(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {genders.map(gen => (
                <MenuItem key={gen} value={gen}>{gen}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Metric Toggles */}
        <Grid item xs={12}>
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Toggle Metrics
            </Typography>
            <Grid container spacing={1}>
              {metrics.map(metric => (
                <Grid item xs={12} sm={6} md={3} key={metric.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleMetrics[metric.id]}
                        onChange={() => handleMetricToggle(metric.id)}
                        sx={{
                          color: metric.color,
                          '&.Mui-checked': {
                            color: metric.color
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{metric.name}</Typography>
                        {velocity[metric.id] !== undefined && (
                          getVelocityIcon(velocity[metric.id])
                        )}
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Line Chart */}
      {roundsData.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={roundsData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="label" 
                label={{ value: 'Survey Round', position: 'bottom', offset: 0 }}
              />
              <YAxis 
                label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                domain={[0, 100]}
              />
              <Tooltip 
                formatter={(value) => `${value.toFixed(1)}%`}
                labelFormatter={(label) => `Survey ${label}`}
              />
              <Legend />
              
              {metrics.map(metric => (
                visibleMetrics[metric.id] && (
                  <Line
                    key={metric.id}
                    type="monotone"
                    dataKey={metric.id}
                    name={metric.name}
                    stroke={metric.color}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                )
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No trend data available for selected filters
          </Typography>
        </Box>
      )}

      {/* Velocity Indicators */}
      {Object.keys(velocity).length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Latest Round Changes
          </Typography>
          <Grid container spacing={1}>
            {metrics.map(metric => (
              visibleMetrics[metric.id] && velocity[metric.id] !== undefined && (
                <Grid item xs={12} sm={6} md={3} key={metric.id}>
                  <Chip
                    icon={getVelocityIcon(velocity[metric.id])}
                    label={`${metric.name}: ${velocity[metric.id] > 0 ? '+' : ''}${velocity[metric.id].toFixed(1)}pp`}
                    size="small"
                    sx={{
                      backgroundColor: Math.abs(velocity[metric.id]) < 1 
                        ? '#e0e0e0' 
                        : velocity[metric.id] > 0 
                        ? `${brandColors.success}20` 
                        : `${brandColors.error}20`,
                      color: Math.abs(velocity[metric.id]) < 1 
                        ? '#757575' 
                        : velocity[metric.id] > 0 
                        ? brandColors.success 
                        : brandColors.error,
                      fontWeight: 600
                    }}
                  />
                </Grid>
              )
            ))}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default MultiMetricTrends;

